import React, { useEffect } from 'react';
import useShowNotification from '../notifications/hooks/useShowNotification';
import styles from './UnsupportedBrowserGuard.module.css';

interface UnsupportedBrowserGuardProps {
  /**
   * Contents to render when the browser is supported.
   */
  children: JSX.Element;
}

const mobileRegexp = /Android|WebOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i;
const forbiddenRegexp = /Edge\//i;
const supportedRegexp = /Chrome|Safari|Firefox/i;
const safari14Regexp = /Version\/14/;
const firefoxRegexp = /\bFirefox\//;

function getBrowserType(userAgent: string | undefined): 'allowed' | 'disallowed' | 'mobile' {
  if (!userAgent) {
    return 'allowed';
  }
  if (mobileRegexp.test(userAgent)) {
    return 'mobile';
  }
  if (supportedRegexp.test(userAgent) && !forbiddenRegexp.test(userAgent)) {
    return 'allowed';
  }
  return 'disallowed';
}

function getBrowserNotification(userAgent: string | undefined): string | null {
  if (!userAgent) {
    return null;
  }
  if (safari14Regexp.test(userAgent)) {
    return 'It seems you are using Safari 14, which has performance issues with SAP Build Apps. Until Apple addresses these issues, we recommend you use Chrome.';
  }
  if (firefoxRegexp.test(userAgent)) {
    return 'We are experiencing UI glitches on some versions of Firefox. If you have issues, please use Chrome instead.';
  }
  return null;
}

export default function UnsupportedBrowserGuard({ children }: UnsupportedBrowserGuardProps): JSX.Element {
  const userAgent = window.navigator?.userAgent;
  const browserType = getBrowserType(userAgent);
  const browserNotification = getBrowserNotification(userAgent);
  const showNotification = useShowNotification();

  useEffect(() => {
    if (browserType !== 'allowed' || !browserNotification) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      showNotification('info', browserNotification);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [browserNotification, browserType, showNotification]);

  if (browserType === 'mobile') {
    return (
      <div className={styles.backdrop}>
        <div className={styles.container}>
          <h1 className={styles.heading}>Mobile use not supported</h1>
          <p className={styles.text}>
            {'SAP Build Apps is not designed to work on mobile devices. Please switch to a '}
            <strong>laptop</strong>
            {' or '}
            <strong>desktop</strong>
            {' computer and come back!'}
          </p>
        </div>
      </div>
    );
  }
  if (browserType === 'disallowed') {
    return (
      <div className={styles.backdrop}>
        <div className={styles.container}>
          <h1 className={styles.heading}>Unsupported browser</h1>
          <p className={styles.text}>
            {'SAP Build Apps currently only supports modern desktop versions of '}
            <strong>
              <a href="https://www.google.com/chrome/" rel="noopener noreferrer">
                Chrome
              </a>
            </strong>
            {', '}
            <strong>
              <a href="https://support.apple.com/downloads/safari" rel="noopener noreferrer">
                Safari
              </a>
            </strong>
            {' and '}
            <strong>
              <a href="https://www.mozilla.org/en-GB/firefox/new/" rel="noopener noreferrer">
                Firefox
              </a>
            </strong>
            . Please switch to a supported browser and come back!
          </p>
        </div>
      </div>
    );
  }
  // Allow the browser and show the contents normally
  return children;
}
